import { reportWidgetOffPage } from "./report-canvas-model"

export function reportNumber(value) {
  if (value == null || typeof value === "boolean" || String(value).trim() === "") return null
  const number = Number(String(value).replace(/,/g, ""))
  return Number.isFinite(number) ? number : null
}

export function reportKpi(table, data) {
  if (!table) return null
  if (data?.aggregation === "count") return table.rows.length
  const raw = table.rows.map((row) => reportNumber(row[data?.metric]))
  if (data?.aggregation === "first") return raw[0] ?? null
  if (data?.aggregation === "last") return raw.at(-1) ?? null
  const values = raw.filter((value) => value !== null)
  if (!values.length) return null
  if (data?.aggregation === "min") return Math.min(...values)
  if (data?.aggregation === "max") return Math.max(...values)
  const total = values.reduce((sum, value) => sum + value, 0)
  return data?.aggregation === "avg" ? total / values.length : total
}

// R9 (DESIGN-LOG §44): the whole rows a table frame `height` px tall shows, from its measured row stops
// (stops[k - 1] = the frame height at which k rows show whole). Preview reads it for its "continues" line and the
// PDF prints exactly these rows on the page, the rest on continuation pages. Null when the rows were not measured.
export function shownTableRows(stops, height) {
  if (!Array.isArray(stops) || !stops.length) return null
  return Math.max(1, stops.filter((stop) => stop <= height + 0.5).length)
}

// Each continuation contains a complete table row and repeats the original header.
// With `dropOffPage` (fixed report pages), widgets past the page bottom are not printed.
// With `stops` (measured row stops by widget id), a table's first page prints the rows Preview shows whole.
export function paginateReportTab(tab, tables, canvas, dropOffPage = false, stops = {}) {
  const widgets = dropOffPage ? (tab.widgets || []).filter((widget) => !reportWidgetOffPage(widget.layout, canvas)) : tab.widgets || []
  const first = { ...tab, widgets, tables: { ...tables } }
  const pages = [first]
  for (const widget of widgets) {
    const table = tables[widget.id]
    if (!["table", "freeTable"].includes(widget.type) || !table) continue
    const capacity = shownTableRows(stops?.[widget.id], widget.layout.h) ?? Math.max(1, Math.floor((widget.layout.h - 80) / 36))
    first.tables[widget.id] = { ...table, rows: table.rows.slice(0, capacity) }
    const height = (canvas?.height || 794) - 170
    const continuationCapacity = Math.max(1, Math.floor((height - 80) / 36))
    // §75: a continuation table spans the page's content, between margins as wide as the leftmost widget's (the grid's
    // 16px or a template's 32): not the header band, which can bleed to the paper's edges (Artyom: «не
    // везде сохраняются отступы по краям»). With no other widget, the old 32px margin.
    const content = widgets.map((item) => item.layout).filter((layout, index) => widgets[index].type !== "header" && Number.isFinite(layout?.x) && Number.isFinite(layout?.w))
    const pageWidth = canvas?.width || 1123
    const left = content.length ? Math.min(...content.map((layout) => layout.x)) : 32
    // The page's margins are even: a page with one half-width table still continues across the content width.
    const right = Math.max(pageWidth - left, ...content.map((layout) => layout.x + layout.w))
    const frame = { x: left, w: Math.max(0, right - left) }
    for (let offset = capacity; offset < table.rows.length; offset += continuationCapacity) {
      pages.push({ id: `${tab.id}-${widget.id}-${offset}`, name: tab.name,
        widgets: [...widgets.filter((item) => item.type === "header"), { ...widget, layout: { x: frame.x, y: 120, w: frame.w, h: height } }],
        tables: { [widget.id]: { ...table, rows: table.rows.slice(offset, offset + continuationCapacity) } } })
    }
  }
  return pages
}

// §75: html2canvas draws text with a letter spacing one letter at a time, from its own measures, and the big figures
// came apart ("89.1 2"): in the copy it draws (only there), the KPI figures and the header's name keep normal spacing.
function evenLetters(doc) {
  const style = doc.createElement("style")
  style.textContent = ".datox-react-dashboard__kpi-value, .datox-react-dashboard__kpi-value *, .datox-react-dashboard__kpi-unit, .datox-react-dashboard__header-content, .datox-react-dashboard__header-content * { letter-spacing: normal !important; }"
  doc.head.append(style)
}

// html2canvas 1.4.1's font probe (its SMALL_IMAGE), the one image its FontMetrics measures a baseline on.
export const HTML2CANVAS_PROBE = "data:image/gif;base64,R0lGODlhAQABAIAAAAAAAP///yH5BAEAAAAALAAAAAABAAEAAAIBRAA7"

export async function renderReportPdf(root, config) {
  await document.fonts.ready
  await Promise.all([...root.querySelectorAll("img")].map((image) => image.decode()))
  await new Promise((resolve, reject) => {
    const deadline = performance.now() + 15000
    const ready = () => {
      const charts = [...root.querySelectorAll("[data-report-chart]")]
      const kpis = [...root.querySelectorAll("[data-report-kpi]")]
      if (kpis.some((kpi) => kpi.dataset.reportKpi === "error")) { reject(new Error("KPI calculation failed")); return }
      if (charts.every((chart) => chart.dataset.reportChart === "ready") && kpis.every((kpi) => kpi.dataset.reportKpi === "ready")) {
        requestAnimationFrame(() => requestAnimationFrame(resolve))
        return
      }
      if (performance.now() > deadline) { reject(new Error("Chart rendering timed out")); return }
      requestAnimationFrame(ready)
    }
    ready()
  })
  const { default: html2canvas } = await import("html2canvas")
  const { jsPDF } = await import("jspdf")
  const portrait = config.canvas?.orientation === "portrait"
  const pdf = new jsPDF({ orientation: portrait ? "portrait" : "landscape", unit: "mm", format: "a4", compress: true })
  const width = pdf.internal.pageSize.getWidth()
  const height = pdf.internal.pageSize.getHeight()
  const pages = [...root.querySelectorAll(".datox-react-dashboard__export-page")]
  if (!pages.length) throw new Error("No export pages")
  // §75: html2canvas measures each font's baseline on a 1×1 probe image in this document (its FontMetrics); the app's
  // `img { display: block }` (Tailwind's preflight) put that image on a line of its own, so the PDF drew every text
  // about 0.6em low and clipped text (widget titles, KPI names, column labels) lost its lower half. The probe stays
  // inline while the pages render.
  const probe = document.createElement("style")
  probe.textContent = `img[src="${HTML2CANVAS_PROBE}"] { display: inline !important; }`
  document.head.append(probe)
  try {
    for (const [index, page] of pages.entries()) {
      const canvas = await html2canvas(page, { backgroundColor: "#ffffff", scale: 2, useCORS: true, onclone: evenLetters })
      if (index) pdf.addPage()
      const scale = Math.min(width / canvas.width, height / canvas.height)
      pdf.addImage(canvas.toDataURL("image/png"), "PNG", 0, 0, canvas.width * scale, canvas.height * scale, undefined, "FAST")
    }
  } finally {
    probe.remove()
  }
  return pdf.output("blob")
}
